import { IModel, IAttribute, Collection } from '../base/annotation';
import { Activity } from './activity';
import { SparkJobDefinition } from '../sparkjobdefinition/sparkjobdefinition';
import { SparkConfiguration } from '../sparkconfiguration/sparkconfiguration';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { Metamodel, Attribute } from '../base/model';
import { ModelConstants } from '../model-constants';
import { ParameterConfig } from './parameter-config';
import { BigDataPool, NodeSize } from '../bigdatapool/bigdatapool';
import { A365BackendModel } from '../a365.backendmodel';
import { format } from '../../shared/utils/string.util';
import { ParameterConfigUtils } from '../adapters/linkedservices/parameter-config.util';
import { AzureSynapseArtifactsLinkedService } from '../linkedservice/azure-synapse-artifacts.linkedservice';
import { ParameterValue } from '../base/parameter-value';
import { Owned } from '../base/named';
import { SynapseActivityAuthenticationType } from './synapse-activity-authentication-type';

@Metamodel.model({
    name: ModelConstants.sparkJobActivity,
    referable: true
})
export class SynapseSparkJobDefinitionActivity extends Activity {
    public static readonly MODEL: IModel;
    public static readonly NAME: IAttribute;
    public static readonly MAIN_DEFINITION_FILE: IAttribute;
    public static readonly MAIN_CLASS_NAME: IAttribute;
    public static readonly ARGUMENTS: IAttribute;
    public static readonly SCAN_FOLDER: IAttribute;
    public static readonly REFERENCE_FILES: IAttribute;
    public static readonly PYTHON_CODE_REFERENCE: IAttribute;
    public static readonly REFERENCE_FILES_V2: IAttribute;
    public static readonly SPARKPOOL: IAttribute;
    public static readonly EXECUTOR_SIZE: IAttribute;
    public static readonly DYNAMIC_EXECUTOR: IAttribute;
    public static readonly MIN_EXECUTORS: IAttribute;
    public static readonly MAX_EXECUTORS: IAttribute;
    public static readonly DRIVER_SIZE: IAttribute;
    public static readonly TARGET_SPARK_CONFIGURATION: IAttribute;
    public static readonly LINKED_SERVICE: Attribute;
    public static readonly LINKED_SERVICE_PARAMETERS: IAttribute;

    @Metamodel.attribute({
        display: shellStringResources.SynapseSparkJobDefinition,
        constraint: {
            required: true
        },
        reference: true,
        association: 'NAME',
        accepts: ['String']
    })
    public sparkJobDefinition: SparkJobDefinition;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMainDefinitionFile,
        templatable: true,
        association: 'MAIN_DEFINITION_FILE'
    })
    public mainDefinitionFile?: string;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMainClassName,
        templatable: true,
        association: 'MAIN_CLASS_NAME'
    })
    public mainClassName?: string;

    @Metamodel.attribute({
        display: shellStringResources.Arguments,
        association: 'ARGUMENTS',
        templatable: true,
        accepts: ['Array']
    })
    public sparkJobDefinitionArguments?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionScanFolder,
        templatable: true,
        association: 'SCAN_FOLDER'
    })
    public scanFolder?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityReferenceFiles,
        association: 'REFERENCE_FILES',
        templatable: true,
        accepts: ['Array']
    })
    public referenceFiles?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityPythonCodeReference,
        association: 'PYTHON_CODE_REFERENCE',
        templatable: true,
        accepts: ['Array']
    })
    public pythonCodeReference?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityReferenceFiles,
        association: 'REFERENCE_FILES_V2',
        templatable: true,
        accepts: ['Array']
    })
    public referenceFilesV2?: ParameterConfig[] = [];

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivitySparkPoolLabel,
        reference: true,
        templatable: true,
        type: BigDataPool.MODEL,
        association: 'SPARKPOOL',
        accepts: ['String']
    })
    public sparkPool: BigDataPool;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityExecutorSizeLabel,
        templatable: true,
        association: 'EXECUTOR_SIZE'
    })
    public executorSize?: NodeSize;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityDynamicExecutorLabel,
        templatable: true,
        association: 'DYNAMIC_EXECUTOR'
    })
    public dynamicExecutor?: boolean;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMinExecutorsLabel,
        templatable: true,
        association: 'MIN_EXECUTORS'
    })
    public minExecutors?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityMaxExecutorsLabel,
        templatable: true,
        association: 'MAX_EXECUTORS'
    })
    public maxExecutors?: number;

    @Metamodel.attribute({
        display: shellStringResources.SparkJobDefinitionActivityDriverSizeLabel,
        templatable: true,
        association: 'DRIVER_SIZE'
    })
    public driverSize?: NodeSize;

    @Metamodel.attribute({
        type: SynapseActivityAuthenticationType.MODEL
    })
    public authentication: SynapseActivityAuthenticationType = Owned.create<SynapseActivityAuthenticationType>(SynapseActivityAuthenticationType.MODEL, this);

    @Metamodel.attribute({
        display: shellStringResources.ApacheSparkConfiguration,
    })
    public configurationType?: A365BackendModel.ConfigurationType;

    @Metamodel.attribute({
        display: shellStringResources.ApacheSparkConfiguration,
        templatable: true,
        association: 'TARGET_SPARK_CONFIGURATION',
        accepts: ['String']
    })
    public targetSparkConfiguration?: SparkConfiguration;

    @Metamodel.attribute({
        display: shellStringResources.ApacheSparkConfiguration,
        constraint: {
            validator: ParameterConfigUtils.createParameterConfigurationValidator()
        }
    })
    public sparkConfig?: ParameterConfig[];

    @Metamodel.attribute({
        display: format(shellStringResources.ModelName_LinkedServiceTemplate, shellStringResources.AzureSynapseAnalytics),
        reference: true,
        association: 'LINKED_SERVICE',
        type: AzureSynapseArtifactsLinkedService.MODEL
    })
    public linkedService?: AzureSynapseArtifactsLinkedService;

    /**
     * Parameter value for referenced linked service.
     */
    @Metamodel.attribute({
        collection: Collection.Array,
        constraint: {
            required: false
        },
        association: 'LINKED_SERVICE_PARAMETERS'
    })
    public linkedServiceParameters: ParameterValue[] = [];
}
